import { dv } from '@aviv-estimation-funnel/data-validation/dv';
import type { FieldValidator } from '@aviv-estimation-funnel/data-validation/field-validator';
import {
    EstateType,
    Job,
    JobRole,
    NonOwnerObjective,
    OwnerObjective,
    PropertyCondition,
    PropertyMainUsage,
    Salutation,
} from '@aviv-estimation-funnel/estimation-data-models/enums';
import { ViewFromProperty } from '@aviv-estimation-funnel/estimation-data-models/enums/view-from-property.enum';

export const commonRegularExpressions = {
    // At least 2 characters and no digits
    Name: /^[^0-9]{2,}$/,
    // Anything (no @ or ") OR anything in quotes | @ once | anything (no @) | . once | anything at least 2 and max 30 (no @)
    // Limit of 30 for the last part is to protect against ReDos attacks / make it non-vulnerable
    // to super-linear runtime due to backtracking.
    Email: /^(?:[^@"]{1,255}|".+")@[^@]+\.[^@]{2,30}$/,
};

export const commonNewEstimationFormFieldsValidators: Record<string, FieldValidator<unknown>> = {
    // Step 1 - Location
    cityName: dv.string().minLength(1),
    streetName: dv.string().minLength(1),

    // Step 2 - Property type
    estateType: dv.enum(EstateType),

    // Step 3 - Characteristics
    livingSurface: dv.float().conditionalFiltering(
        (fieldValues) => fieldValues['estateType'] === EstateType.Apartment,
        [dv.filters.range(4, 2_000)], // Apartment
        [dv.filters.range(4, 10_000)], // House
    ),
    totalSiteSurface: dv
        .float()
        .if((fieldValues) => fieldValues['estateType'] === EstateType.House)
        .optional()
        .range(1, 1_000_000),
    numberOfBathRooms: dv.integer().range(0, 100),
    floorNumber: dv
        .integer()
        .if((fieldValues) => fieldValues['estateType'] === EstateType.Apartment)
        .range(0, 100),
    numberOfFloorsInBuilding: dv.integer().range(1, 100).greaterOrEqualThan('floorNumber'),
    monthlyCondominiumFee: dv
        .float()
        .if((fieldValues) => fieldValues['estateType'] === EstateType.Apartment)
        .optional()
        .range(1, 3_000),

    // Step 4 - Benefits
    // Common
    // TODO: VEGA-817 Remove this field
    hasABalcony: dv.boolean().optional(),
    // TODO: VEGA-817 Remove this field
    balconiesSurface: dv
        .float()
        .if((fieldValues) => fieldValues['hasABalcony'] === true)
        .optional()
        .range(1, 1_000),
    hasABalconyOrTerrace: dv
        .boolean()
        // TODO: VEGA-817 Remove this condition
        .if((fieldValues) => fieldValues['hasABalcony'] === undefined),
    balconiesAndTerracesSurface: dv
        .float()
        .if((fieldValues) => fieldValues['hasABalconyOrTerrace'] === true)
        .optional()
        .range(1, 1_000),
    hasAParking: dv.boolean().optional(),
    hasAnIndoorParking: dv.boolean().if((fieldValues) => fieldValues['hasAParking'] === undefined),
    hasAnOutdoorParking: dv.boolean().if((fieldValues) => fieldValues['hasAParking'] === undefined),
    numberOfParkingLots: dv
        .integer()
        .if((fieldValues) => fieldValues['hasAParking'] === true)
        .optional()
        .range(1, 100),
    numberOfIndoorParkingLots: dv
        .integer()
        .if((fieldValues) => fieldValues['hasAnIndoorParking'] === true)
        .range(1, 100),
    numberOfOutdoorParkingLots: dv
        .integer()
        .if((fieldValues) => fieldValues['hasAnOutdoorParking'] === true)
        .range(1, 100),
    hasAnOutstandingView: dv.boolean().optional(),
    viewFromProperty: dv
        .enum(ViewFromProperty)
        .if((fieldValues) => fieldValues['hasAnOutstandingView'] === undefined),

    // Apartment
    hasACellar: dv
        .boolean()
        .if((fieldValues) => fieldValues['estateType'] === EstateType.Apartment),
    hasAnElevator: dv
        .boolean()
        .if((fieldValues) => fieldValues['estateType'] === EstateType.Apartment),
    hasAPrivateGarden: dv
        .boolean()
        .if((fieldValues) => fieldValues['estateType'] === EstateType.Apartment),
    privateGardensSurface: dv
        .float()
        .if((fieldValues) => fieldValues['hasAPrivateGarden'] === true)
        .optional()
        .range(1, 100_000),
    // TODO: VEGA-817 Remove this field
    hasATerrace: dv
        .boolean()
        .if((fieldValues) => fieldValues['estateType'] === EstateType.Apartment)
        .optional(),
    // TODO: VEGA-817 Remove this field
    terracesSurface: dv
        .float()
        .if((fieldValues) => fieldValues['hasATerrace'] === true)
        .optional()
        .range(1, 300),
    // House
    hasABasement: dv.boolean().if((fieldValues) => fieldValues['estateType'] === EstateType.House),
    basementsSurface: dv
        .float()
        .if((fieldValues) => fieldValues['hasABasement'] === true)
        .optional()
        .range(1, 10_000),
    hasASwimmingPool: dv
        .boolean()
        .if((fieldValues) => fieldValues['estateType'] === EstateType.House),

    // Step 5 - Conditions
    averageCondition: dv.enum(PropertyCondition),
    constructionPeriodMin: dv
        .integer()
        .if((fieldValues) => fieldValues['estateType'] === EstateType.Apartment)
        .optional()
        .range(1_000, new Date().getUTCFullYear()),
    constructionPeriodMax: dv
        .integer()
        .if((fieldValues) => fieldValues['estateType'] === EstateType.Apartment)
        .optional()
        .range(1_000, new Date().getUTCFullYear()),

    // Step 6 - Your project
    isAPrivatePerson: dv.boolean(),
    isPropertyOwner: dv.boolean().if((fieldValues) => fieldValues['isAPrivatePerson'] === true),
    propertyMainUsage: dv
        .enum(PropertyMainUsage)
        .if((fieldValues) => fieldValues['isPropertyOwner'] === true),
    ownerObjective: dv
        .enum(OwnerObjective)
        .if((fieldValues) => fieldValues['isPropertyOwner'] === true),
    nonOwnerObjective: dv
        .enum(NonOwnerObjective)
        .if((fieldValues) => fieldValues['isPropertyOwner'] === false),
    job: dv.enum(Job).if((fieldValues) => fieldValues['isAPrivatePerson'] === false),
    jobRole: dv.enum(JobRole).if((fieldValues) => fieldValues['job'] === Job.Realtor),

    // Step 6 - Your profile
    salutation: dv.enum(Salutation),
    firstName: dv.string().format(commonRegularExpressions.Name),
    lastName: dv.string().format(commonRegularExpressions.Name),
    email: dv.string().format(commonRegularExpressions.Email),
    hasAcceptedTermsAndConditions: dv.boolean().equal(true),
};

export const commonRenewValuationFormFieldsValidators: Record<string, FieldValidator<unknown>> = {
    isPropertyOwner: dv.boolean(),
    propertyMainUsage: commonNewEstimationFormFieldsValidators['propertyMainUsage'],
    ownerObjective: commonNewEstimationFormFieldsValidators['ownerObjective'],
    nonOwnerObjective: commonNewEstimationFormFieldsValidators['nonOwnerObjective'],
};
